import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { toast } from "sonner";

export interface SparePartType {
  id: string;
  company_id: string | null;
  is_system_default: boolean;
  parts_category_id: string;
  name: string;
  description: string | null;
  default_weight_kg: number | null;
  default_value: number | null;
  default_recovery_pct: number | null;
  is_hazardous: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface SparePartTypeAlias {
  id: string;
  part_type_id: string;
  alias: string;
  language: string | null;
  source: string | null;
  brand: string | null;
}

export interface SparePartTag {
  id: string;
  company_id: string | null;
  name: string;
  color: string | null;
  description: string | null;
}

const db = supabase as any;

export function usePartTypes(partsCategoryId: string | undefined) {
  return useQuery({
    queryKey: ["spare_part_types", partsCategoryId],
    enabled: !!partsCategoryId,
    queryFn: async (): Promise<SparePartType[]> => {
      const { data, error } = await db
        .from("spare_part_types")
        .select("*")
        .eq("parts_category_id", partsCategoryId)
        .order("name");
      if (error) throw error;
      return data || [];
    },
  });
}

export function useAliases(partTypeId: string | undefined) {
  return useQuery({
    queryKey: ["spare_part_type_aliases", partTypeId],
    enabled: !!partTypeId,
    queryFn: async (): Promise<SparePartTypeAlias[]> => {
      const { data, error } = await db
        .from("spare_part_type_aliases")
        .select("*")
        .eq("part_type_id", partTypeId)
        .order("alias");
      if (error) throw error;
      return data || [];
    },
  });
}

export function useAllTags() {
  return useQuery({
    queryKey: ["spare_part_tags"],
    queryFn: async (): Promise<SparePartTag[]> => {
      const { data, error } = await db.from("spare_part_tags").select("*").order("name");
      if (error) throw error;
      return data || [];
    },
  });
}

export function useTypeTagIds(partTypeId: string | undefined) {
  return useQuery({
    queryKey: ["spare_part_type_tags", partTypeId],
    enabled: !!partTypeId,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await db
        .from("spare_part_type_tags")
        .select("tag_id")
        .eq("part_type_id", partTypeId);
      if (error) throw error;
      return (data || []).map((r: any) => r.tag_id);
    },
  });
}

export function useLinkedInstanceCount(partTypeId: string | undefined) {
  const { id: companyId } = useCurrentCompany();
  return useQuery({
    queryKey: ["spare_parts_count_by_type", partTypeId, companyId],
    enabled: !!partTypeId && !!companyId,
    queryFn: async (): Promise<number> => {
      const { count, error } = await db
        .from("spare_parts")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId!)
        .eq("part_type_id", partTypeId);
      if (error) throw error;
      return count || 0;
    },
  });
}

export function useUnlinkedSpareParts() {
  const { id: companyId } = useCurrentCompany();
  return useQuery({
    queryKey: ["spare_parts_unlinked", companyId],
    enabled: !!companyId,
    queryFn: async () => {
      const { data, error } = await db
        .from("spare_parts")
        .select("id, name, brand, model_number, part_number, serial_no, harvested_at, appliance_id, appliances:appliance_id(category)")
        .eq("company_id", companyId!)
        .is("part_type_id", null)
        .order("harvested_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data || [];
    },
  });
}

export function usePartTypeMutations(partsCategoryId: string | undefined) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["spare_part_types", partsCategoryId] });
    qc.invalidateQueries({ queryKey: ["spare_parts_unlinked"] });
  };

  const create = useMutation({
    mutationFn: async (payload: Partial<SparePartType> & { name: string }) => {
      const { error } = await db.from("spare_part_types").insert({
        ...payload,
        parts_category_id: partsCategoryId,
        is_system_default: true,
        company_id: null,
      });
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Part type added"); },
    onError: (e: any) => toast.error(e.message || "Failed to add"),
  });

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<SparePartType> }) => {
      const { error } = await db.from("spare_part_types").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Part type updated"); },
    onError: (e: any) => toast.error(e.message || "Failed to update"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db.from("spare_part_types").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Part type removed"); },
    onError: (e: any) => toast.error(e.message || "Failed to delete"),
  });

  return { create, update, remove };
}

export function useAliasMutations(partTypeId: string | undefined) {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["spare_part_type_aliases", partTypeId] });

  const add = useMutation({
    mutationFn: async (payload: Omit<SparePartTypeAlias, "id">) => {
      const { error } = await db.from("spare_part_type_aliases").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => { inv(); toast.success("Alias added"); },
    onError: (e: any) => toast.error(e.message || "Failed to add alias"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db.from("spare_part_type_aliases").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { inv(); },
    onError: (e: any) => toast.error(e.message || "Failed to remove alias"),
  });

  return { add, remove };
}

export function useTagMutations(partTypeId: string | undefined) {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: ["spare_part_tags"] });
    qc.invalidateQueries({ queryKey: ["spare_part_type_tags", partTypeId] });
  };

  const createTag = useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await db
        .from("spare_part_tags")
        .insert({ name: name.trim(), company_id: null })
        .select()
        .single();
      if (error) throw error;
      return data as SparePartTag;
    },
    onSuccess: () => inv(),
    onError: (e: any) => toast.error(e.message || "Failed to create tag"),
  });

  const link = useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await db
        .from("spare_part_type_tags")
        .insert({ part_type_id: partTypeId, tag_id: tagId });
      if (error) throw error;
    },
    onSuccess: () => inv(),
    onError: (e: any) => toast.error(e.message || "Failed to link tag"),
  });

  const unlink = useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await db
        .from("spare_part_type_tags")
        .delete()
        .eq("part_type_id", partTypeId)
        .eq("tag_id", tagId);
      if (error) throw error;
    },
    onSuccess: () => inv(),
    onError: (e: any) => toast.error(e.message || "Failed to unlink tag"),
  });

  const linkUnlinkedPart = useMutation({
    mutationFn: async ({ sparePartId, partTypeId }: { sparePartId: string; partTypeId: string }) => {
      const { error } = await db
        .from("spare_parts")
        .update({ part_type_id: partTypeId })
        .eq("id", sparePartId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["spare_parts_unlinked"] });
      toast.success("Linked");
    },
    onError: (e: any) => toast.error(e.message || "Failed to link"),
  });

  return { createTag, link, unlink, linkUnlinkedPart };
}

export function useLinkUnlinkedPart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sparePartId, partTypeId }: { sparePartId: string; partTypeId: string }) => {
      const { error } = await db
        .from("spare_parts")
        .update({ part_type_id: partTypeId })
        .eq("id", sparePartId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["spare_parts_unlinked"] });
      toast.success("Linked to part type");
    },
    onError: (e: any) => toast.error(e.message || "Failed to link"),
  });
}
